import {
  Banner,
  Button,
  FlexBox,
  IconButton,
  Page,
  SimplePagination,
  Text,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useRef } from 'react';
import { useEffectOnce } from 'react-use';

import { appActions } from 'app/app.actions';
import { websocketActions } from 'app/middleware/websocket/actions/websocket';
import { articles } from 'utils/elevio';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useServerStorage } from 'utils/useServerStorage';

import { DashboardHeartbeat } from '../../components/heartbeat/dashboardHeartbeat';

import { QueueDashboardControls } from './components/controls/queueDashboardControls';
import { QueueDashboardEmpty } from './components/empty/queueDashboardEmpty';
import { QueueDashboardError } from './components/error/queueDashboardError';
import { QueueDashboardLoading } from './components/loading/queueDashboardLoading';
import { QueueDashboardQueues } from './components/queues/queueDashboardQueues';
import { useQueueDashboardQueues } from './utils/useQueueDashboardQueues';

export const QueueDashboardView = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    asyncStatus,
    items: queues,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
  } = useQueueDashboardQueues();

  const storage = useServerStorage('user');
  const { layout = 'default', isNewDashboardBannerDismissed = false } =
    storage.data.dashboard ?? {};

  useEffectOnce(() => {
    ampli.dashboardViewed({ version: 'new', layout });

    dispatch(
      websocketActions.request({
        type: 'connection:subscribe',
        params: {
          type: 'real_time_dashboard',
          dashboard_type: 'queue',
        },
      }),
    );

    return () => {
      dispatch(
        websocketActions.request({
          type: 'connection:unsubscribe',
          params: {
            type: 'real_time_dashboard',
            dashboard_type: 'queue',
          },
        }),
      );
    };
  });

  const handleNextPage = useCallback(() => {
    nextPage();
    ampli.dashboardNextPageClicked();
  }, [nextPage]);

  const handlePreviousPage = useCallback(() => {
    previousPage();
    ampli.dashboardPreviousPageClicked();
  }, [previousPage]);

  const handleSendFeedback = useCallback(() => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'app:feedbackForm',
          arg: {
            title: 'Dashboard Feedback',
            team: 'insights',
          },
        },
      }),
    );
  }, []);

  const renderContent = (): JSX.Element => {
    switch (asyncStatus) {
      case 'uninitialized':
      case 'pending':
        return (
          <QueueDashboardLoading
            contentHeight={contentRef.current?.clientHeight}
          />
        );

      case 'rejected':
        return <QueueDashboardError />;

      case 'fulfilled':
      default: {
        if (queues.length === 0) {
          return <QueueDashboardEmpty />;
        }

        return (
          <>
            <QueueDashboardQueues queues={queues} />

            {(hasNextPage || hasPreviousPage) && (
              <SimplePagination
                previousButtonClick={handlePreviousPage}
                previousButtonDisabled={!hasPreviousPage}
                nextButtonClick={handleNextPage}
                nextButtonDisabled={!hasNextPage}
              />
            )}
          </>
        );
      }
    }
  };

  return (
    <Page>
      <Page.Head
        title={
          <FlexBox as="span" alignItems="center">
            Real-time dashboard
            <DashboardHeartbeat />
            <Text.ExternalLink onClick={handleSendFeedback} fontSize="200">
              Send feedback
            </Text.ExternalLink>
          </FlexBox>
        }
        controls={<QueueDashboardControls />}
      />

      <Page.Body ref={contentRef}>
        <Page.Section
          callout={
            !isNewDashboardBannerDismissed && (
              <Banner
                variant="announcement"
                headline="The new real-time dashboard is here"
                description={
                  <Text.Paragraph as="span" variant="4">
                    The new Dashboard brings enhanced real-time metrics,
                    insightful SLA analysis, timezone sensitivity, drill-down
                    capabilities and customization options.
                  </Text.Paragraph>
                }
                actionSlot={
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        elevioClient.openArticle(
                          articles.dashboard.documentation,
                        );
                      }}
                    >
                      Learn more
                    </Button>

                    <IconButton
                      icon="close"
                      variant="tertiary"
                      onClick={() => {
                        storage.set({
                          dashboard: (draft) => {
                            draft.isNewDashboardBannerDismissed = true;
                          },
                        });
                      }}
                    />
                  </>
                }
              />
            )
          }
        >
          <FlexBox flexDirection="column" gap="300" height="100%">
            {renderContent()}
          </FlexBox>
        </Page.Section>
      </Page.Body>
    </Page>
  );
};
